<script setup lang="ts">
import { useDirectUpload } from "@/composables/useDirectUpload";
import type { File as ApiFile } from "@/services/fobizzToolsApi";
import { type FileInput } from "@/services/fobizzToolsApi";
import { type RailsBlob } from "@/types";
import { flash } from "@/utils/flash";

const props = withDefaults(
  defineProps<{
    allowedTypes?: string[];
    attachmentName: string;
    maxFileSize?: number;
    maxFilesCount?: number;
    showEmbeddedProgressbar?: boolean;
    fixedProgressbar?: boolean;
    multiple?: boolean;
    disabled?: boolean;
    shrine?: boolean;
  }>(),
  {
    allowedTypes: () => [],
    maxFileSize: 10,
    maxFilesCount: undefined,
    showEmbeddedProgressbar: false,
    fixedProgressbar: false,
    multiple: false,
    disabled: false,
    shrine: false,
  }
);

const emit = defineEmits<{
  "upload:error": [error: unknown];
  "upload:success": [fileData: FileInput | Partial<ApiFile>];
  "upload:progress": [progress: number];
}>();

const { t } = useI18n();

const { uploadFile: uploadFileViaActiveStorage } = useDirectUpload();
const { uploadFile: uploadFileViaShrine } = useDirectUploadShrine();
const progress = ref(0);
const isDragging = ref<boolean>(false);
const inputRef = ref<HTMLElement>();
const dropTargetRef = ref<HTMLElement>();

const upload = (file: File) => {
  if (props.shrine) {
    uploadViaShrine(file);
  } else {
    uploadViaActiveStorage(file);
  }
};

const uploadViaShrine = (file: File) => {
  uploadFileViaShrine(file, {
    errorHandler: (error: unknown) => {
      emit("upload:error", error);
      progress.value = 0;
    },
    successHandler: (data: FileInput) => {
      emit("upload:success", data);
      progress.value = 0;
    },
    progressHandler: (_progress: number) => {
      progress.value = _progress;
      emit("upload:progress", progress.value);
    },
  });
};

const uploadViaActiveStorage = (file: File) => {
  uploadFileViaActiveStorage(file, {
    errorHandler: (error: unknown) => {
      emit("upload:error", error);
      progress.value = 0;
    },
    successHandler: (blob: RailsBlob) => {
      emit("upload:success", {
        id: blob.id,
        key: blob.key,
        filename: blob.filename,
        contentType: blob.content_type,
        byteSize: blob.byte_size,
        signedId: blob.signed_id,
      });
      progress.value = 0;
    },
    progressHandler: (_progress: number) => {
      progress.value = _progress;
      emit("upload:progress", progress.value);
    },
  });
};

const allowedMimeTypesTypes = computed(() =>
  props.allowedTypes.filter((t) => t.includes("/"))
);

const allowedFileExtensions = computed(() =>
  props.allowedTypes.filter((t) => t.startsWith(".")).map((t) => t.substring(1))
);

const getFileExtension = (filename: string): string => {
  return filename.substring(filename.lastIndexOf(".")).toLowerCase();
};

// file.size is in bytes
// props.maxFileSize is in megabytes
const handleUpload = (file: File) => {
  const fileSize = file?.size || 0;
  if (fileSize === 0) {
    flash(FlashKindEnum.ALERT, t("errors.uploadFileTooSmall"));
    return;
  }

  if (file.size / 1024 > props.maxFileSize * 1000) {
    const errorMessage = t("errors.uploadFileTooLarge", {
      maxSize: props.maxFileSize,
    });
    flash(FlashKindEnum.ALERT, errorMessage);
    emit("upload:error", errorMessage);
    return;
  }

  const fileExtension = getFileExtension(file.name).substring(1); // Remove the dot
  if (props.allowedTypes.length > 0) {
    if (
      allowedMimeTypesTypes.value.includes(file.type) ||
      allowedFileExtensions.value.includes(fileExtension)
    ) {
      upload(file);
    } else {
      emit(
        "upload:error",
        `${t("hints.allowedFileTypes")}: ${props.allowedTypes.join(", ")}`
      );
    }
  } else {
    upload(file);
  }
};

const dragover = (_event: DragEvent) => {
  if (props.disabled) return;

  isDragging.value = true;
};

const dragleave = (event: DragEvent) => {
  if (props.disabled) return;

  // every child element triggers dragleave
  // only stop dragging of mouseover parent element
  if (event.target === dropTargetRef.value) {
    isDragging.value = false;
  }
};

const dragend = () => {
  if (props.disabled) return;

  isDragging.value = false;
};

function onDrop(event: DragEvent) {
  if (props.disabled) return;

  const files = Array.from(event?.dataTransfer?.files || []);
  if (props.multiple) {
    files.forEach((file) => handleUpload(file));
  } else {
    handleUpload(files[0]);
  }
  isDragging.value = false;
}

function handleInputChange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input) {
    const files = Array.from(input.files || []);
    if (props.multiple) {
      const allowedFiles = props.maxFilesCount
        ? files.slice(0, props.maxFilesCount)
        : files;
      allowedFiles.forEach((file) => handleUpload(file));
    } else {
      handleUpload(files[0]);
    }
  }
}

function openFileBrowser(): void {
  inputRef.value?.click();
}

const clearFiles = () => {
  if (inputRef.value) {
    (inputRef.value as HTMLInputElement).value = "";
  }
};

const acceptAllowedTypes = computed(() => {
  if (props.allowedTypes) {
    return props.allowedTypes.join(", ");
  }
  return "";
});
</script>

<template>
  <div
    ref="dropTargetRef"
    class="dropzone-overlay"
    :class="{
      'dropzone-overlay--dragging': isDragging,
      'dropzone-overlay--fixed-progress-bar': fixedProgressbar,
    }"
    role="button"
    tabindex="0"
    @drop.prevent.stop="onDrop"
    @dragover.prevent="dragover"
    @dragleave.prevent="dragleave"
    @dragend.prevent="dragend"
  >
    <slot v-bind="{ openFileBrowser, progress, clearFiles }"></slot>
    <div
      v-if="showEmbeddedProgressbar && progress > 0"
      class="dropzone-overlay__progress"
    >
      <div class="dropzone-overlay__progress-message">
        <p class="font-xs fw-semibold p-1 mb-0">{{ t("hints.uploading") }}</p>
        <BaseProgressBar :progress="progress" />
      </div>
    </div>
    <input
      ref="inputRef"
      type="file"
      aria-label="Hiddeen file input"
      :style="{ display: 'none' }"
      data-testid="input-file"
      :accept="acceptAllowedTypes"
      :multiple="multiple"
      @change="handleInputChange"
    />
    <div class="dropzone-overlay__indicator">
      <div class="dropzone-overlay__message">
        <div class="mb-3">
          <i class="bi bi-upload"></i>
        </div>
        <p>{{ t("hints.drop") }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.dropzone-overlay {
  position: relative;

  &__indicator {
    position: absolute;
    top: 25%;
    left: 0;
    right: 0;
    display: none;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 2;
  }

  &__message {
    width: 320px;
    max-width: 100%;
    padding: $spacer-4;
    border-radius: $border-radius-lg;
    text-align: center;
    font-size: 1.5rem;
    color: $color-white;
    background-color: rgb(0 0 0 / 75%);
  }

  &--dragging &__indicator {
    display: flex;
  }

  &--dragging {
    background-color: rgb(0 0 0 / 7.5%);
  }

  &__progress {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &--fixed-progress-bar &__progress {
    position: fixed;
    inset: 0;
  }

  &__progress-message {
    width: 320px;
    max-width: 100%;
    padding: $spacer-4;
    border-radius: $border-radius-lg;
    text-align: center;
    font-size: 1.5rem;
    color: $color-white;
    background-color: rgb(0 0 0 / 75%);
  }
}
</style>
